<script module>
  import AppLayout from '../../layouts/AppLayout.svelte'
  export const layout = AppLayout
</script>

<script>
  import { router } from '@inertiajs/svelte'
  import { Button } from '$lib/components/ui/button/index.js'
  import PageContainer from '$lib/components/page-container.svelte'
  import { Input } from '$lib/components/ui/input/index.js'
  import { Label } from '$lib/components/ui/label/index.js'
  import { Progress } from '$lib/components/ui/progress/index.js'
  import { Alert, AlertDescription } from '$lib/components/ui/alert/index.js'
  import { Card, CardHeader, CardTitle, CardContent, CardAction } from '$lib/components/ui/card/index.js'
  import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter, SheetDescription
  } from '$lib/components/ui/sheet/index.js'
  import PencilIcon from '@lucide/svelte/icons/pencil'
  import XIcon from '@lucide/svelte/icons/x'

  let { property, errors = {} } = $props()

  const TAXES = [
    { field: 'assessment_tax_cents',  cardTitle: 'Cukai Taksiran',  subtitle: 'Assessment Tax (MBPJ / DBKL)', metaLabel: 'Annual Amount (RM)', label: 'Assessment Tax',  helper: 'Enter the annual assessment tax amount.' },
    { field: 'quit_rent_tax_cents',   cardTitle: 'Cukai Tanah',     subtitle: 'Quit Rent (Land Office)',      metaLabel: 'Annual Amount (RM)', label: 'Quit Rent',       helper: 'Enter the annual quit rent amount.' },
    { field: 'service_charges_cents', cardTitle: 'Service Charges', subtitle: 'Monthly management fees',      metaLabel: 'Annual Total (RM)',  label: 'Service Charges', helper: 'Enter the total annual service charges.' },
    { field: 'sinking_fund_cents',    cardTitle: 'Sinking Fund',    subtitle: 'Reserve fund contributions',   metaLabel: 'Annual Total (RM)',  label: 'Sinking Fund',    helper: 'Enter the annual sinking fund amount.' },
  ]

  // ── Mobile sheet state ────────────────────────────────────────────────
  let editSheetOpen = $state(false)
  let activeTax = $state(null)
  let amountRm = $state('')
  let submitting = $state(false)

  // ── Desktop inline state ──────────────────────────────────────────────
  let localAmounts = $state(
    Object.fromEntries(TAXES.map(t => [t.field, (property[t.field] / 100).toFixed(2)]))
  )
  let savingField = $state(null)

  function formatRM(cents) {
    return (cents / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  // Mobile sheet handlers
  function openEditSheet(tax) {
    activeTax = tax
    amountRm = (property[tax.field] / 100).toFixed(2)
    editSheetOpen = true
  }

  function handleSave() {
    if (submitting) return
    submitting = true
    const cents = Math.round(parseFloat(amountRm || '0') * 100)
    router.put(`/properties/${property.id}/statutory_taxes`, {
      statutory_taxes: { field: activeTax.field, amount_cents: cents }
    }, {
      preserveState: false,
      onSuccess: () => { editSheetOpen = false },
      onFinish: () => { submitting = false }
    })
  }

  // Desktop blur-to-save handler
  function handleTaxBlur(tax) {
    const newCents = Math.round(parseFloat(localAmounts[tax.field] || '0') * 100)
    if (newCents === property[tax.field]) return
    savingField = tax.field
    router.put(`/properties/${property.id}/statutory_taxes`, {
      statutory_taxes: { field: tax.field, amount_cents: newCents }
    }, {
      preserveState: true,
      preserveScroll: true,
      viewTransition: false,
      onFinish: () => { savingField = null }
    })
  }
</script>

<svelte:head>
  <title>Statutory Taxes — MyRentalTax</title>
</svelte:head>

<PageContainer>
  {#snippet breadcrumb()}
    <span class="text-[11px] font-medium text-muted-foreground">Statutory Taxes · Step 3 of 5</span>
  {/snippet}
    <!-- Header -->
    <div class="flex flex-col gap-1">
      <h1 class="text-xl sm:text-2xl font-semibold text-foreground m-0">Statutory Taxes & Property Management</h1>
      <p class="text-sm text-muted-foreground m-0 leading-relaxed">Enter your annual property tax and management fees. These are fully tax-deductible against rental income.</p>
    </div>

    <!-- Progress row (desktop) -->
    <div class="hidden sm:flex items-center gap-3">
      <span class="text-xs font-medium text-muted-foreground">Step 3 of 5</span>
      <Progress value={3} max={5} class="flex-1" />
    </div>

    <!-- Tax cards — 2×2 grid on desktop, stacked on mobile -->
    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {#each TAXES as tax}
        <Card class="shadow-none border-border">
          <CardHeader class="px-4 sm:px-6 py-3 sm:py-4">
            <CardTitle class="text-sm font-semibold">{tax.cardTitle}</CardTitle>
            <!-- Pencil edit button — mobile only -->
            <CardAction class="sm:hidden">
              <Button
                variant="ghost"
                size="icon"
                class="h-7 w-7 text-muted-foreground"
                onclick={() => openEditSheet(tax)}
                aria-label="Edit {tax.label}"
              >
                <PencilIcon class="w-4 h-4" />
              </Button>
            </CardAction>
          </CardHeader>

          <!-- Desktop: inline editable input -->
          <div class="hidden sm:block px-6 py-4">
            {#if tax.subtitle}
              <p class="text-xs text-muted-foreground mb-2">{tax.subtitle}</p>
            {/if}
            <div class="flex flex-col gap-1.5">
              <Label for="tax-inline-{tax.field}">{tax.metaLabel}</Label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">RM</span>
                <Input
                  id="tax-inline-{tax.field}"
                  type="number"
                  min="0"
                  step="0.01"
                  bind:value={localAmounts[tax.field]}
                  class="h-10 pl-10"
                  disabled={savingField === tax.field}
                  onblur={() => handleTaxBlur(tax)}
                />
              </div>
              {#if savingField === tax.field}
                <p class="text-xs text-muted-foreground m-0">Saving…</p>
              {/if}
            </div>
          </div>

          <!-- Mobile: value display -->
          <CardContent class="sm:hidden px-4 py-4 flex flex-col gap-1">
            {#if tax.subtitle}
              <p class="text-xs text-muted-foreground m-0">{tax.subtitle}</p>
            {/if}
            <p class="text-xs text-muted-foreground m-0 mt-1">{tax.metaLabel}</p>
            <p class="text-base font-semibold text-foreground m-0">{formatRM(property[tax.field])}</p>
          </CardContent>
        </Card>
      {/each}
    </div>

    {#if activeTax && errors[activeTax.field]}
      <Alert variant="destructive">
        <AlertDescription>{errors[activeTax.field][0]}</AlertDescription>
      </Alert>
    {/if}

  {#snippet footer()}
    <Button variant="outline" onclick={() => router.visit(`/properties/${property.id}/property_finance`)}>Back</Button>
    <div class="flex-1"></div>
    <Button onclick={() => router.visit(`/properties/${property.id}/utilities`)}>Next: Utilities →</Button>
  {/snippet}
</PageContainer>

<!-- Edit tax sheet (mobile only) -->
<Sheet bind:open={editSheetOpen}>
  <SheetContent side="bottom" class="p-0 rounded-t-2xl" showCloseButton={false}>
    <!-- Drag handle -->
    <div class="flex justify-center pt-2">
      <div class="w-9 h-1 rounded-full bg-muted-foreground opacity-40"></div>
    </div>

    <SheetHeader class="flex-row items-center justify-between px-4 py-3 border-b border-border gap-0.5">
      <SheetTitle>{activeTax?.label ?? ''}</SheetTitle>
      <SheetDescription class="sr-only">{activeTax?.helper ?? ''}</SheetDescription>
      <Button variant="ghost" size="icon" class="h-6 w-6 text-muted-foreground -mr-1" onclick={() => { editSheetOpen = false }} aria-label="Close">
        <XIcon class="w-4 h-4" />
      </Button>
    </SheetHeader>

    <div class="px-4 py-5 flex flex-col gap-3">
      <div class="flex flex-col gap-1.5">
        <Label for="tax-input">Amount (RM)</Label>
        <div class="relative">
          <span class="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">RM</span>
          <Input
            id="tax-input"
            type="number"
            min="0"
            step="0.01"
            bind:value={amountRm}
            class="h-10 pl-10"
          />
        </div>
        {#if activeTax}
          <p class="text-xs text-muted-foreground m-0">{activeTax.helper}</p>
        {/if}
      </div>

      {#if activeTax && errors[activeTax.field]}
        <Alert variant="destructive">
          <AlertDescription>{errors[activeTax.field][0]}</AlertDescription>
        </Alert>
      {/if}
    </div>

    <SheetFooter class="flex-row px-4 pb-6 pt-0 gap-3 border-t border-border">
      <Button variant="outline" onclick={() => { editSheetOpen = false }} class="flex-shrink-0">Cancel</Button>
      <Button onclick={handleSave} disabled={submitting} class="flex-1">
        {submitting ? 'Saving…' : 'Save'}
      </Button>
    </SheetFooter>
  </SheetContent>
</Sheet>
